import Vue from 'vue';
import { Component, Prop, Emit } from 'vue-property-decorator';
import {
  TicketListingViewModel,
  ITicketChannelParticipant,
  ITicketChannel
} from '../../../../models/ticket';
import { DateTimeHelper } from '../../../../utils/DateTimeHelper.util';
import { StateTypes } from '../../../../common/enums';
import {namespace} from "vuex-class";
import {TICKET_MODULE_NAMESPACE, TICKET_LOADERS} from "../../../../store/ticket";

const TicketStore = namespace(TICKET_MODULE_NAMESPACE);

interface IChatTeam {
  icon: string;
  translationKey?: string;
  records?: any[];
  key?: string;
}

@Component({
  components: {
    'the-seperator': async () => import('../ticket-chat-message-item-seperator/TicketChatMessageItemSeperator.vue'),
    'chat-header-loader': async () => import('../../loaders/ticket-chat-header-loader/TicketChatHeaderLoader.vue')
  }
})
class TicketChatHeader extends Vue {

  dtActionOpen: boolean;
  dtConfirmationVisible: boolean;
  dtError: any;
  dtTeams: IChatTeam[];
  dtShowManager?: boolean;
  dtShowApartment?: boolean;
  dtTemporaryParticipant: any;
  dtExpandedValues: boolean[];
  dtShowZones?:boolean
  /**
   *
   */
  constructor() {
    super();
    this.dtActionOpen = false;
    this.dtShowManager = false;
    this.dtShowApartment = false;
    this.dtTemporaryParticipant = null;
    this.dtConfirmationVisible = false;
    this.dtError = null;
    this.dtTeams = [
      { key: 'ticketMembers', icon: 'icon-team', translationKey: 'TICKET_MEMBERS', records: [] },
      { key: 'participants', icon: 'icon-team', translationKey: 'TICKET_MEMBER_CHANNEL', records: [] },
      { key: 'apartments', icon: 'icon-blocks', translationKey: 'TICKET_APARTMENTS' },
      { key: 'apartment_zone', icon: 'gps_fixed', translationKey: 'TICKET_ZONES' },
      { key: 'equipments', icon: 'handyman', translationKey: 'TICKET_EQUIPMENTS' },
      { key: 'attachments', icon: 'attach_file', translationKey: this.lblSetting.ticketAttachments },
      { key: 'close', icon: 'icon-diagnostic', translationKey: this.lblSetting.ticketAdditionalActions },
    ]
    this.dtExpandedValues = this.dtTeams.map(x => false);
    this.dtShowZones = false
  }

  @TicketStore.Getter currentChannel: ITicketChannel;
  @TicketStore.Getter loaders: Map<string,boolean>


  @Prop({ required: true, type: [Object] })
  ticket: TicketListingViewModel;
  @Prop({ required: true, type: [Number] })
  userId: number;
  @Prop({ default: () => [], type: [Array] })
  ticketMembers: any[];
  @Prop({ default: () => [], type: [Array] })
  participants: ITicketChannelParticipant[];

  @Prop({ default: () => [], type: [Array] })
  attachments: any[];

  get coCreatedAt() {
    return DateTimeHelper.parseDate(this.ticket.createdAt)
  }
  get coTeams(): IChatTeam[] {
    let res = this.dtTeams.map(team => {
      team.records = this[team.key] || [];

      return team;
    })
    if (!this.ticket.equipments || this.ticket.equipments.length === 0)
      res = res.filter(x => x.key !== 'equipments')
    if (!this.ticket.apartment_zone || this.ticket.apartment_zone.length === 0)
      res = res.filter(x => x.key !== 'apartment_zone')
    if (!this.ticket.apartments || this.ticket.apartments.length === 0)
      res = res.filter(x => x.key !== 'apartments')
    if (!this.attachments || this.attachments.length === 0)
      res = res.filter(x => x.key !== 'attachments')
    return res
  }
  get coProblemTypology() {
    return {
      level_1: this.ticket.fk_ticket_character_problem_name,
      level_2: this.ticket.fk_character_problem_level_2_name
    }
  }
  get coLoader(): boolean {
    return this.loaders.get(TICKET_LOADERS.CHAT_HEADER)
  }
  get lblSetting() {
    return require('@/locales/ticket').default
  }
  get coTitle(): string {
    return this.ticket.subject;
  }
  get coNotClosed(): boolean {
    const result = (this.ticket.state === StateTypes.Close);
    return !result;
  }
  get coBlockName(): string {
    return this.ticket.block.name;
  }
  get coApartmentName(): string {
    if (!this.ticket.apartments) return ''
    if (this.ticket.apartments.length > 0) {
      if (this.ticket.apartments.length === 1) {
        return '- ' + this.apartmentFormatted(this.ticket.apartments[0])
      } else if (this.ticket.apartments.length === 2) {
        return '- ' + this.apartmentFormatted(this.ticket.apartments[0]) + this.$t(this.lblSetting.ticketOneMoreValue)
      } else if (this.ticket.apartments.length > 2) {
        return '- ' + this.apartmentFormatted(this.ticket.apartments[0]) + ' + ' + (this.ticket.apartments.length - 1) + this.$t(this.lblSetting.ticketOthersValue)
      }
    }
    return null;
  }
  get coTicketCode(): string {
    return this.ticket.pb_type_code;
  }
  get coCreatedDate(): string {
    return DateTimeHelper.parseDate(this.ticket.createdAt);
  }
  get coFullNameDeclarant(): string {
    //return this.handleGetObjectName(this.ticket.creator[0]);
    return this.handleGetObjectName(this.ticket.declarant[0]);
  }
  get coFullNameManager(): string {
    return this.handleGetObjectName(this.ticket.managers_assigned[0]);
  }
  get coOrderMangers() {
    return this.ticket.managers_assigned.filter((x: AnalyserNode, index: number) => index !== 0)
      .map(x => ({ id: x.id, text: `${x.firstname || ''} ${x.lastname || ''}` }))
  }
  get coOrderApartment() {
    if (!this.ticket.apartments) return []
    return this.ticket.apartments.filter((x: AnalyserNode, index: number) => index !== 0)
      .map(x => ({ id: x.id, text: this.apartmentFormatted(x) }))
  }
  get problemName(): string {
    return this.ticket.fk_ticket_character_problem_name;
  }
  get name(): string {
    if (this.dtTemporaryParticipant) {
      return `${this.dtTemporaryParticipant.firstname} ${this.dtTemporaryParticipant.lastname}`
    }
    return null;
  }
  get coAttachments() {
    return this.attachments;
  }
  get removeParticipantMsg() {
    if (!this.dtTemporaryParticipant)
      return ''
    if (this.dtTemporaryParticipant.type === 'independent_contractor' || this.dtTemporaryParticipant.type === 'caretaker') {
      if (this.currentChannel.type === 'public') {
        return this.$t('LBL_REMOVE_PARTICIPANT',{name: this.name}) + '</BR>' + this.$t('LBL_REMOVE_PARTICIPANT_IC_FROM_GENERAL')
      } else {
        return this.$t('LBL_REMOVE_PARTICIPANT',{name: this.name}) + '</BR>' + this.$t('LBL_REMOVE_PARTICIPANT_IC_FROM_PRIVATE')
      }
    }
    return this.$t('LBL_REMOVE_PARTICIPANT',{name: this.name})
  }

  get canRemoveParticipants(): boolean {
    return this.$route.meta.remove_participant_channel;
  }

  get canUpdateTicket():boolean {
    const { update_ticket_agency, update_ticket_self }= this.$route.meta
    return (update_ticket_agency && this.ticket.as_actor && this.ticket.as_actor.length && this.ticket.as_actor[0].id === this.userId) ||
        //(update_ticket_self && this.ticket.creator && this.ticket.creator.length && this.ticket.creator[0].id === this.userId )
        (update_ticket_self && this.ticket.declarant && this.ticket.declarant.length && this.ticket.declarant[0].id === this.userId )
  }

  get coZones():string {
    if(this.ticket.apartment_zone.length > 0){
      if(this.ticket.apartment_zone.length === 1)
        return ' - ' +this.ticket.apartment_zone[0].name
      if(this.ticket.apartment_zone.length === 2)
        return ' - ' + this.ticket.apartment_zone[0].name + this.$t(this.lblSetting.ticketOneMoreValue)
      if(this.ticket.apartment_zone.length > 2)
        return ' - ' + this.ticket.apartment_zone[0].name + ' + ' + (this.ticket.apartment_zone.length - 1) + this.$t(this.lblSetting.ticketOthersValue)
    } else {
      return '';
    }
  }
  get coOrderZones() {
    return this.ticket.apartment_zone.filter((x: AnalyserNode, index: number) => index !== 0)
    .map(x => ({ id: x.id, text: x.name }))
  }
  get coTicketSubstatus() {
    return {
      haveSubstatus :this.ticket.fk_ticket_substatus ? true:false,
      name : this.ticket.substatus ? this.ticket.substatus.name : null
    }
  }
  getVisibility(team, event) {
    return team.key !== 'ticketMembers' && this.$route.meta.remove_participant_channel
      && this.participants.filter(x => x.id === this.userId).length > 0
  }

  handleGetObjectName(obj: any) {
    if (obj)
      return `${obj.firstname} ${obj.lastname}`;
    return '';
  }

  handleGetCreatedAt(object: any) {
    return DateTimeHelper.parseDateTime(object.createdAt)
  }
  @Emit('reopen')
  handleReopenTicket() {
  }

  @Emit('duplicate')
  handleDuplicateTicket () {
  }

  @Emit('update')
  handleUpdateTicket(){}

  _clearTimeout: any
  handleRemoveParticipants() {
    clearTimeout(this._clearTimeout)
    this._clearTimeout = setTimeout(() => {
      this.dtConfirmationVisible = false;
      if (this.dtTemporaryParticipant) {
        this.$emit('remove', JSON.parse(JSON.stringify(this.dtTemporaryParticipant)))
      }
    }, 300);
  }

  onBeforeRemove(participant) {
    clearTimeout(this._clearTimeout)
    this._clearTimeout = setTimeout(() => {
      this.dtConfirmationVisible = true;
      this.dtTemporaryParticipant = participant;
    }, 300);

  }

  onCancel() {
    this.dtConfirmationVisible = false;
    this.dtTemporaryParticipant = null;
  }

  closeVisibility(key: string) {
    return key === 'close'
  }
  attachmentVisibility(key: string) {
    return key === 'attachments'
  }
  apartmentsVisibility(key: string) {
    return key === 'apartments'
  }
  equipmentsVisibility(key: string) {
    return key === 'equipments'
  }
  zonesVisibility(key: string) {
    return key === 'apartment_zone'
  }
  apartmentFormatted(apt) {
    if (apt.location_number)
      return apt.location_number + ' / ' + apt.apartment_number
    return apt.apartment_number
  }
  equipmentFormatted(eqp) {
    if (eqp.reference)
      return '('+eqp.reference+') '+eqp.name
    return eqp.name
  }
  zoneFormatted(zone) {
    return zone.name
  }
  get coTicketPriorityLevel(): string {
    return this.ticket.ticket_priority
  }
  handelEquipmentLink(equipment) {
    let equipmentType = equipment.fk_character_problem_lvl1 === 238 ? 'vehicle': 'equipment';
    return `/block/${this.ticket.fk_ticket_block}/${equipmentType}/${equipment.id}`;
  }
}

export default TicketChatHeader;



// WEBPACK FOOTER //
// ./src/containers/ticket/components/ticket-chat-header/index.ts